import { useState } from "react";
import { Outlet } from "react-router-dom";
import { SectionTabs } from "@/components/SectionTabs";
import { MenuLateral, MenuMobile } from "@/components/MenuNav";
import { LayoutDashboard, Upload, GitMerge, Receipt, FileDown, Settings, LogOut, Landmark, CalendarRange, BookOpenCheck, Search, SlidersHorizontal, MessageSquareText, Send, FolderOpen, CalendarDays, Building2, ArrowLeftRight, Percent, Scale, Calculator, MailQuestion, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ReservationSearch } from "@/components/ReservationSearch";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { to: "/imports", label: "Imports", icon: Upload, id: "imports" },
  { to: "/fichiers", label: "Fichiers", icon: FolderOpen, id: "fichiers" },
  { to: "/rapprochement", label: "Rapprochement", icon: GitMerge, id: "rapprochement" },
  { to: "/reservations", label: "Réservations & taxes", icon: Receipt, id: "reservations" },
  { to: "/calcul-reservation", label: "Calculer une réservation", icon: Calculator, id: "calcul-reservation" },
  { to: "/demandes", label: "Demandes aux plateformes", icon: MailQuestion, id: "demandes" },
  { to: "/calendrier", label: "Calendrier", icon: CalendarDays, id: "calendrier" },
  { to: "/comparaison", label: "Comparaison de mois", icon: CalendarRange, id: "comparaison" },
  { to: "/bilan", label: "Bilan annuel", icon: BookOpenCheck, id: "bilan" },
  { to: "/appartements", label: "Bilan par appartement", icon: Building2, id: "appartements" },
  { to: "/recherche", label: "Recherche relevés", icon: Search, id: "recherche" },
  { to: "/commentaires", label: "Commentaires opérations", icon: MessageSquareText, id: "commentaires" },
  { to: "/declaration-taxes", label: "Déclaration TPS & TVQ", icon: Percent, id: "declaration-taxes" },
  { to: "/conciliation-bancaire", label: "Conciliation bancaire", icon: Scale, id: "conciliation-bancaire" },
  { to: "/reaffecter", label: "Réaffecter les opérations", icon: ArrowLeftRight, id: "reaffecter" },
  { to: "/exports", label: "Exports comptables", icon: FileDown, id: "exports" },
  { to: "/envoi-comptable", label: "Envoi au comptable", icon: Send, id: "envoi-comptable" },
  { to: "/plateformes", label: "Paramètres plateformes", icon: SlidersHorizontal, id: "plateformes" },
  { to: "/parametres", label: "Paramètres", icon: Settings, id: "parametres" },
];

const linkCls = ({ isActive }) =>
  `group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors duration-150 ${
    isActive ? "bg-emerald-500/15 text-emerald-300" : "text-slate-400 hover:bg-white/5 hover:text-white"
  }`;

const Brand = () => (
  <div className="flex items-center gap-2.5">
    <div className="h-8 w-8 rounded-md bg-emerald-600 grid place-items-center">
      <Landmark className="h-4 w-4 text-white" />
    </div>
    <div className="leading-tight">
      <p className="text-sm font-bold text-white tracking-tight">Rapproch.</p>
      <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Airbnb · Booking</p>
    </div>
  </div>
);

const ETATS = ["complet", "icones", "cache"];
const TITRES = { complet: "Réduire le menu aux icônes", icones: "Masquer le menu", cache: "Afficher le menu complet" };
const POSITIONS = {
  complet: "absolute right-2 top-2 h-7 w-7 text-slate-500 hover:bg-white/10 hover:text-white",
  icones: "mx-auto h-7 w-7 text-slate-500 hover:bg-white/10 hover:text-white",
  cache: "fixed left-2 top-2 z-40 h-8 w-8 bg-slate-950 text-slate-300 shadow-md hover:text-white",
};

const lireEtat = () => {
  const e = localStorage.getItem("menu_etat");
  if (ETATS.includes(e)) { return e; }
  return localStorage.getItem("menu_cache") === "1" ? "cache" : "complet";
};

const BoutonMenu = ({ etat, basculer }) => {
  const Icone = etat === "cache" ? PanelLeftOpen : PanelLeftClose;
  return (
    <button
      onClick={basculer}
      title={TITRES[etat]}
      aria-label={TITRES[etat]}
      className={`hidden lg:grid shrink-0 place-items-center rounded-md transition-colors duration-150 ${POSITIONS[etat]}`}
      data-testid="sidebar-toggle-button"
      data-etat={etat}
    >
      <Icone className="h-4 w-4" />
    </button>
  );
};

const PiedMenu = ({ user, signOut, compact }) => (
  <div className={`mt-4 shrink-0 border-t border-white/10 pt-4 ${compact ? "flex justify-center" : ""}`}>
    {!compact && <p className="truncate text-xs text-slate-400" data-testid="sidebar-user-email">{user?.email}</p>}
    <button
      onClick={signOut}
      title="Se déconnecter"
      className="mt-2 flex items-center gap-2 text-xs text-slate-500 hover:text-white transition-colors"
      data-testid="logout-button"
    >
      <LogOut className="h-3.5 w-3.5" />{!compact && " Se déconnecter"}
    </button>
  </div>
);

export default function Layout() {
  const { user, signOut } = useAuth();
  const [etat, setEtat] = useState(lireEtat);
  const basculer = () => setEtat((e) => {
    const suivant = ETATS[(ETATS.indexOf(e) + 1) % ETATS.length];
    localStorage.setItem("menu_etat", suivant);
    return suivant;
  });
  const compact = etat === "icones";
  return (
    <div className="min-h-screen lg:flex">
      {etat === "cache" && <BoutonMenu etat="cache" basculer={basculer} />}
      <aside className={`${etat === "cache" ? "hidden" : "hidden lg:flex"} ${compact ? "w-16 px-2" : "w-[17rem] px-4"} shrink-0 flex-col bg-slate-950 py-6 sticky top-0 h-screen`} data-testid="sidebar" data-etat={etat}>
        <BoutonMenu etat={compact ? "icones" : "complet"} basculer={basculer} />
        {compact ? (
          <div className="mt-3 mx-auto h-8 w-8 rounded-md bg-emerald-600 grid place-items-center"><Landmark className="h-4 w-4 text-white" /></div>
        ) : (
          <><Brand /><div className="mt-6"><ReservationSearch /></div></>
        )}
        <MenuLateral nav={NAV} linkCls={linkCls} compact={compact} />
        <PiedMenu user={user} signOut={signOut} compact={compact} />
      </aside>
      <header className="lg:hidden bg-slate-950 px-4 pt-4 pb-2 sticky top-0 z-30">
        <div className="flex items-center justify-between">
          <Brand />
          <button onClick={signOut} className="text-slate-400" data-testid="mobile-logout-button">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-3"><ReservationSearch testId="mobile-resa-search" /></div>
        <MenuMobile nav={NAV} linkCls={linkCls} />
      </header>
      <main id="contenu-principal" className={`flex-1 min-w-0 px-4 py-6 sm:px-8 lg:px-10 lg:py-10 ${etat === "cache" ? "lg:pl-14" : ""}`}>
        <SectionTabs />
        <Outlet />
      </main>
    </div>
  );
}
